import React, { useMemo, useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, PanelHeader, KPICard } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { QrCode as QrIcon, ScanLine, CheckCircle2, Camera, Keyboard } from "lucide-react";
import { toast } from "sonner";
import { Html5Qrcode } from "html5-qrcode";

// Lightweight inline SVG QR placeholder (a stylized matrix) — checkin happens by member_id click for demo
function QRBlock({ payload }) {
  // Pseudo QR drawing using hashed rows (purely visual, real check-in via clicks)
  const rows = 21;
  const grid = useMemo(() => {
    let h = 0;
    for (let i = 0; i < payload.length; i++) h = ((h << 5) - h + payload.charCodeAt(i)) | 0;
    const cells = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < rows; c++) {
        const v = (h ^ (r * 31 + c * 17)) & 1;
        cells.push({ r, c, v });
      }
    }
    return cells;
  }, [payload]);
  return (
    <div className="bg-white p-3 rounded-sm inline-block">
      <svg width="160" height="160" viewBox={`0 0 ${rows} ${rows}`}>
        {grid.map((g, i) => (
          <rect key={i} x={g.c} y={g.r} width="1" height="1" fill={g.v ? "#000" : "#fff"} />
        ))}
        {/* finder squares */}
        {[[0, 0], [0, rows - 7], [rows - 7, 0]].map(([r, c], i) => (
          <g key={i} fill="#000">
            <rect x={c} y={r} width="7" height="7" />
            <rect x={c + 1} y={r + 1} width="5" height="5" fill="#fff" />
            <rect x={c + 2} y={r + 2} width="3" height="3" />
          </g>
        ))}
      </svg>
    </div>
  );
}

export default function AttendancePage() {
  const { user, activeBranchId } = useAuth();
  const qc = useQueryClient();
  const [scanOpen, setScanOpen] = useState(false);
  const [scanQuery, setScanQuery] = useState("");

  const params = activeBranchId ? { branch_id: activeBranchId } : {};

  const { data: heatmap = [] } = useQuery({
    queryKey: ["att-heatmap", activeBranchId],
    queryFn: async () => (await apiClient.get("/attendance/heatmap", { params: { ...params, days: 30 } })).data,
  });
  const { data: recent = [] } = useQuery({
    queryKey: ["att-recent", activeBranchId],
    queryFn: async () => (await apiClient.get("/attendance", { params: { ...params, days: 1 } })).data,
  });
  const { data: members = [] } = useQuery({
    queryKey: ["members-att", activeBranchId],
    queryFn: async () => (await apiClient.get("/members", { params })).data,
  });

  const checkIn = useMutation({
    mutationFn: async (member_id) => (await apiClient.post("/attendance/check-in", {
      member_id, branch_id: activeBranchId || members.find((m) => m.user_id === member_id)?.branch_id,
    })).data,
    onSuccess: (a, member_id) => {
      const m = members.find((x) => x.user_id === member_id);
      toast.success(`✓ Checked in: ${m?.name || "Member"}`);
      qc.invalidateQueries({ queryKey: ["att-recent"] });
      qc.invalidateQueries({ queryKey: ["att-heatmap"] });
    },
    onError: () => toast.error("Check-in failed"),
  });

  const total30 = heatmap.reduce((s, h) => s + h.count, 0);
  const today = new Date().toISOString().slice(0, 10);
  const todayCount = heatmap.find((h) => h.date === today)?.count || 0;
  const max = Math.max(...heatmap.map((h) => h.count), 1);

  // Absentee buckets
  const buckets = { d7: 0, d14: 0, d21: 0 };
  const now = Date.now();
  members.forEach((m) => {
    if (!m.last_visit) return;
    const days = Math.floor((now - new Date(m.last_visit).getTime()) / (1000 * 60 * 60 * 24));
    if (days >= 21) buckets.d21++;
    else if (days >= 14) buckets.d14++;
    else if (days >= 7) buckets.d7++;
  });

  const filteredMembers = scanQuery
    ? members.filter((m) => m.name?.toLowerCase().includes(scanQuery.toLowerCase()) || m.phone?.includes(scanQuery))
    : members.slice(0, 12);

  return (
    <div className="p-8 space-y-6 animate-fade-in-up" data-testid="attendance-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-volt font-semibold">Attendance</div>
          <h1 className="font-display text-5xl font-black uppercase tracking-tight mt-1">Coach Check-In</h1>
          <div className="text-zinc-500 text-sm mt-1">Coaches scan each member&apos;s personal QR · attendance auto-attributed to the coach.</div>
        </div>
        <Dialog open={scanOpen} onOpenChange={setScanOpen}>
          <DialogTrigger asChild>
            <Button data-testid="open-scan-button" className="bg-volt hover:bg-volt-hover text-black font-bold uppercase tracking-wider rounded-sm">
              <ScanLine size={16} className="mr-1" /> Open Scanner
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0a0a0a] border-zinc-800 text-white max-w-md" data-testid="scanner-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl uppercase tracking-tight">Coach Scanner</DialogTitle>
              <DialogDescription className="text-zinc-500 text-xs">Point the camera at the member&apos;s Check-In QR — or search manually below.</DialogDescription>
            </DialogHeader>
            <QrCameraScanner
              members={members}
              onScanned={(memberId) => {
                const m = members.find((x) => x.user_id === memberId);
                if (m) { checkIn.mutate(m.user_id); setScanOpen(false); }
                else toast.error("QR did not match any member in your list");
              }}
            />
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold pt-3 border-t border-zinc-800">Or search manually</div>
            <Input placeholder="Search member by name or phone…" value={scanQuery} onChange={(e) => setScanQuery(e.target.value)}
              className="bg-zinc-950 border-zinc-800" data-testid="scan-search-input" />
            <div className="max-h-72 overflow-y-auto scrollbar-thin space-y-1">
              {filteredMembers.map((m) => (
                <button key={m.id} onClick={() => checkIn.mutate(m.user_id)} disabled={checkIn.isPending}
                  data-testid={`checkin-${m.user_id}`}
                  className="w-full flex items-center justify-between bg-zinc-950 border border-zinc-900 hover:border-volt p-2 rounded-sm">
                  <div className="text-left">
                    <div className="text-sm font-semibold">{m.name}</div>
                    <div className="text-[10px] text-zinc-500">{m.phone}</div>
                  </div>
                  <CheckCircle2 size={16} className="text-volt" />
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard testId="kpi-today-att" label="Today's Check-ins" value={todayCount} sub="Across selected branch(es)" />
        <KPICard testId="kpi-30day-att" label="Last 30 Days" value={total30} sub="Total check-ins" />
        <KPICard testId="kpi-absent-14" label="Absent 7-13 Days" value={buckets.d7} accent="warning" sub="Coach follow-up" />
        <KPICard testId="kpi-absent-21" label="Absent 21+ Days" value={buckets.d21} accent="danger" sub="Management alert" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Panel className="lg:col-span-5">
          <PanelHeader subtitle="Coach Workflow" title="Scan Member QR" />
          <div className="space-y-3">
            <div className="text-xs text-zinc-400 leading-relaxed">
              <b className="text-volt">Coaches check members in</b> — open the scanner and pick the member to log their attendance. Each check-in is auto-tagged with the coach who logged it, so we know who actually saw the member on the floor.
            </div>
            <ul className="text-xs text-zinc-400 space-y-1 list-disc list-inside">
              <li>Members show their <b>personal QR code</b> (visible on their Customer Hub).</li>
              <li>Coach scans → instant check-in, attendance % updates.</li>
              <li>Auto-flag absentees at <b className="text-yellow-400">7</b> · <b className="text-orange-400">14</b> · <b className="text-red-400">21</b> day thresholds.</li>
            </ul>
            <Button onClick={() => setScanOpen(true)} data-testid="open-scanner-cta"
              className="w-full bg-volt hover:bg-volt-hover text-black font-bold uppercase tracking-wider rounded-sm">
              <ScanLine size={16} className="mr-2" /> Open Coach Scanner
            </Button>
          </div>
        </Panel>

        <Panel className="lg:col-span-7">
          <PanelHeader subtitle="Last 30 days" title="Attendance Heatmap" />
          <div className="grid grid-cols-10 gap-1.5" data-testid="heatmap-grid">
            {Array.from({ length: 30 }).map((_, i) => {
              const date = new Date();
              date.setDate(date.getDate() - (29 - i));
              const ds = date.toISOString().slice(0, 10);
              const c = heatmap.find((h) => h.date === ds)?.count || 0;
              const intensity = c / max;
              const bg = c === 0 ? "#0a0a0a" : `rgba(204,255,0,${0.15 + intensity * 0.85})`;
              return (
                <div key={i} title={`${ds}: ${c}`} data-testid={`heatmap-cell-${i}`} className="aspect-square rounded-sm border border-zinc-900"
                  style={{ background: bg }} />
              );
            })}
          </div>
          <div className="mt-4 flex items-center gap-3 text-[10px] uppercase tracking-[0.18em] text-zinc-500">
            <span>Less</span>
            {[0.1, 0.3, 0.5, 0.7, 1].map((o, i) => (
              <span key={i} className="w-4 h-4 rounded-sm" style={{ background: `rgba(204,255,0,${o})` }} />
            ))}
            <span>More</span>
          </div>
        </Panel>
      </div>

      <Panel>
        <PanelHeader subtitle="Today" title="Recent Check-Ins" />
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">
              <tr className="border-b border-zinc-900">
                <th className="text-left py-2 px-2 font-semibold">Time</th>
                <th className="text-left py-2 px-2 font-semibold">Member</th>
                <th className="text-left py-2 px-2 font-semibold">Method</th>
              </tr>
            </thead>
            <tbody>
              {recent.slice(0, 20).map((r) => {
                const m = members.find((x) => x.user_id === r.member_id);
                return (
                  <tr key={r.id} className="border-b border-zinc-900">
                    <td className="py-2 px-2 font-mono-cmd text-xs">{new Date(r.check_in).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</td>
                    <td className="py-2 px-2">{m?.name || r.member_id.slice(0, 8)}</td>
                    <td className="py-2 px-2"><span className="text-[10px] uppercase tracking-[0.16em] text-volt">{r.method}</span></td>
                  </tr>
                );
              })}
              {recent.length === 0 && <tr><td colSpan={3} className="py-8 text-center text-zinc-600">No check-ins yet today</td></tr>}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

/* --- Live camera QR scanner for coaches --- */
function QrCameraScanner({ onScanned }) {
  const containerId = "qr-camera-container";
  const scannerRef = useRef(null);
  const [status, setStatus] = useState("idle"); // idle | starting | scanning | error
  const [errMsg, setErrMsg] = useState("");
  const [cameraId, setCameraId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setStatus("starting");
      try {
        const devices = await Html5Qrcode.getCameras();
        if (cancelled) return;
        if (!devices || devices.length === 0) {
          setStatus("error"); setErrMsg("No camera detected on this device.");
          return;
        }
        // Prefer rear/back camera when available
        const rear = devices.find((d) => /back|rear|environment/i.test(d.label)) || devices[devices.length - 1];
        setCameraId(rear.id);
        const scanner = new Html5Qrcode(containerId, { verbose: false });
        scannerRef.current = scanner;
        await scanner.start(
          rear.id,
          { fps: 10, qrbox: { width: 220, height: 220 } },
          (decoded) => {
            // Fire once per successful decode, then stop
            try { scanner.stop().then(() => scanner.clear()).catch(() => {}); } catch { /* noop */ }
            scannerRef.current = null;
            onScanned && onScanned(decoded);
          },
          () => { /* scan-in-progress errors are fine */ }
        );
        if (!cancelled) setStatus("scanning");
      } catch (e) {
        if (cancelled) return;
        setStatus("error");
        setErrMsg(e?.message || "Could not open the camera. Please allow camera access in your browser and try again.");
      }
    })();
    return () => {
      cancelled = true;
      const s = scannerRef.current;
      if (s) {
        try { s.stop().then(() => s.clear()).catch(() => {}); } catch { /* noop */ }
        scannerRef.current = null;
      }
    };
  }, []);

  return (
    <div data-testid="qr-camera-scanner">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-2">
        <Camera size={12} className="text-volt" />
        {status === "scanning" ? "Camera active — align QR inside the box" :
         status === "starting" ? "Starting camera…" :
         status === "error" ? <span className="text-red-400">{errMsg}</span> : "Preparing…"}
      </div>
      <div id={containerId} className="w-full rounded-sm overflow-hidden bg-black" style={{ minHeight: 240 }} />
      {status === "error" && (
        <div className="mt-2 text-[10px] text-zinc-500 flex items-center gap-1"><Keyboard size={10} /> Use the manual search below instead.</div>
      )}
    </div>
  );
}

